import React, { useContext, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent } from "motion/react";
import { IconArrowRight, IconMenu2, IconX } from "@tabler/icons-react";
import {
  Button,
  Separator,
  Sheet,
  SheetContent,
  SheetClose,
  cn,
  useIsMobile,
  useMediaQuery,
  breakpoints,
} from "@lemonadetraining/web-ui";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { AuthContext } from "@lemonadetraining/web-shared";
import CartBadgeButton from "../components/Cart/CartBadgeButton";
import UserAvatarAndPopoverMenu from "./UserAvatarAndPopoverMenu";
import { openDashboard } from "./openDashboard";
import lemonLogo from "../assets/lemon-logo.png";

/* ------- Types */
interface NavigationPage {
  label: string;
  path: string;
}

interface RootState {
  user: {
    isLoggedIn: boolean;
  };
}

// Pages with a black background, where the bar can be transparent over the page
const DARK_PAGES = [
  "/",
  "/lemonade-business",
  "/lemonade-coach",
  "/for-athletes",
  "/about-us",
  "/privacy-policy",
  "/terms-and-conditions",
  "/accessibility",
  "/payment-status",
];

/* ------- Navigation Pages */
const navigationPages: NavigationPage[] = [
  { label: "Lemonade Business", path: "/lemonade-business" },
  { label: "Lemonade Coach", path: "/lemonade-coach" },
  { label: "For Athletes", path: "/for-athletes" },
  { label: "About Us", path: "/About-Us" },
];

/* Skip to content link component */
function SkipToContentLink() {
  return (
    <a
      href="#main-content"
      className="absolute -left-[9999px] z-[9999] rounded bg-black px-6 py-3 text-base font-semibold text-white no-underline focus:left-4 focus:top-4"
    >
      Skip to main content
    </a>
  );
}

/* Mobile Menu Component */
interface MobileMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pages: NavigationPage[];
  goToPage: (path: string) => void;
  isLoggedIn: boolean;
  onSignup: () => void;
  onSignIn: () => void;
}

function MobileMenu({
  open,
  onOpenChange,
  pages,
  goToPage,
  isLoggedIn,
  onSignup,
  onSignIn,
}: MobileMenuProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="w-full max-w-full border-none bg-gradient-to-br from-black/[0.98] to-zinc-900/[0.98] p-0 backdrop-blur-xl sm:max-w-full"
      >
        {/* Close button */}
        <SheetClose
          className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-white backdrop-blur-lg hover:bg-white/20 hover:rotate-90 hover:scale-110 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
          aria-label="Close menu"
        >
          <IconX className="size-6" />
        </SheetClose>

        {/* Menu content */}
        <div className="flex h-full flex-col px-6 pt-24 pb-8">
          <nav aria-label="Mobile navigation" className="flex-1">
            <div className="flex flex-col gap-2">
              {pages.map((page) => (
                <Button
                  key={page.path}
                  variant="ghost"
                  onClick={() => {
                    goToPage(page.path);
                    onOpenChange(false);
                  }}
                  className="h-14 justify-start rounded-2xl px-5 text-xl font-semibold text-white hover:bg-white/15 hover:translate-x-2 transition-all duration-300 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
                >
                  {page.label}
                </Button>
              ))}
            </div>
          </nav>

          <Separator className="my-6 bg-white/20" />

          {isLoggedIn && (
            <Button
              size="lg"
              onClick={() => {
                onOpenChange(false);
                openDashboard();
              }}
            >
              Dashboard
              <IconArrowRight className="size-4" />
            </Button>
          )}

          {!isLoggedIn && (
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  onSignIn();
                  onOpenChange(false);
                }}
                className="border-white/60 text-white hover:bg-white/10"
              >
                Sign in
              </Button>
              <Button
                size="lg"
                onClick={() => {
                  onSignup();
                  onOpenChange(false);
                }}
              >
                Create account
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* Header Component */
function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const authCtx = useContext(AuthContext);
  const isMobile = useIsMobile();
  // The full row (four links, Create account, or cart + Dashboard + avatar) needs ~1200px;
  // anything narrower gets the menu button like phones
  const compactNav = !useMediaQuery(breakpoints.xl);
  const isUserLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up");
  const [atTop, setAtTop] = useState(true);
  // Phones: a fixed bar that hides on scroll down and turns to glass. Wider screens: a static bar
  // that scrolls away with the page, so it never needs the glass box.
  // The bare (transparent) bar has white text, so it is only used on the black marketing pages.
  const onDarkPage = DARK_PAGES.includes(location.pathname.toLowerCase().replace(/(.)\/+$/, "$1"));
  const bare = onDarkPage && (!isMobile || atTop);

  /* Framer Motion scroll detection */
  const { scrollY } = useScroll();
  const lastScrollY = useRef(0);

  useMotionValueEvent(scrollY, "change", (latest) => {
    // Determine scroll direction - hide header when scrolling down, show when scrolling up
    if (latest > lastScrollY.current && latest > 50) {
      setScrollDirection("down");
    } else if (latest < lastScrollY.current) {
      setScrollDirection("up");
    } else if (latest <= 50) {
      // Always show header at the top
      setScrollDirection("up");
    }

    setAtTop(latest <= 16);
    lastScrollY.current = latest;
  });

  /* Navigation handlers */
  const goToPage = (path: string) => {
    navigate(path, { state: { from: location.pathname } });
  };

  const handleLogoClick = () => {
    navigate("/", { state: { from: location.pathname } });
  };

  const handleSignup = () => {
    navigate("/register", { state: { from: location.pathname } });
  };

  const handleSignIn = () => {
    navigate("/login", { state: { from: location.pathname } });
  };

  /* Sign out */
  const signOutTheUser = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (authCtx as any).userLogOut();
    navigate("/");
  };

  /* Active page detection */
  const isActivePage = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <>
      <SkipToContentLink />
      <motion.header
        initial={{ y: 0 }}
        animate={{
          // Phones hide the bar while scrolling down to save height; wider screens keep it in place
          y: isMobile && scrollDirection === "down" ? "-150%" : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
        className={cn(
          isMobile ? "fixed" : "absolute",
          "z-[1200] h-14 rounded-[20px]",
          "top-4 left-4 right-4 md:top-6 md:left-8 md:right-8",
          "w-[calc(100%-2rem)] md:w-[calc(100%-240px)] max-w-[1400px] mx-auto",
          "border transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300",
          bare
            ? "border-transparent bg-transparent shadow-none"
            : !onDarkPage
              ? "border-white/10 bg-zinc-950"
              : "bg-[rgba(57,63,76,0.3)] backdrop-blur-xl backdrop-saturate-[180%] border-white/[0.08] shadow-[0_4px_24px_rgba(0,0,0,0.4),0_2px_8px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.05)]",
          isMobile && scrollDirection === "down" ? "pointer-events-none" : "pointer-events-auto"
        )}
      >
        <div
          className={cn(
            "relative z-[1] flex h-full items-center justify-between transition-[margin,padding] duration-300",
            // Without the box, the content aligns with the page edges
            bare ? "-mx-px pl-0 pr-0" : "pl-[9px] pr-[11px]"
          )}
        >
          {/* Logo Section - Left Aligned */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              handleLogoClick();
            }}
            className={cn(
              "flex min-h-11 min-w-11 cursor-pointer items-center rounded-lg p-0.5 transition-[color,background-color,margin] duration-300 hover:bg-primary/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              bare && "-ml-0.5"
            )}
            aria-label="Lemonade Training - Go to homepage"
          >
            <img
              src={lemonLogo}
              alt="Lemonade Training Logo"
              className="h-8 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation - Left Aligned */}
          {!compactNav && (
            <nav
              aria-label="Primary navigation"
              className="ml-4 hidden flex-1 items-center gap-2.5 md:flex lg:gap-2.5"
            >
              {navigationPages.map((page) => {
                const active = isActivePage(page.path);
                return (
                  <Button
                    key={page.path}
                    variant="ghost"
                    onClick={() => goToPage(page.path)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "relative h-9 rounded-lg px-4 text-sm font-medium",
                      active ? "font-semibold text-white" : "text-white/80 hover:text-white",
                      "hover:bg-white/10"
                    )}
                  >
                    {page.label}
                    {/* Active indicator */}
                    {active && (
                      <span className="absolute bottom-1 left-4 right-4 h-0.5 rounded-full bg-primary" />
                    )}
                  </Button>
                );
              })}
            </nav>
          )}

          {/* Utility Actions - Right Aligned */}
          <div className="flex min-w-fit items-center gap-2 sm:gap-4">
            {/* Mobile Menu Toggle */}
            {compactNav && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation menu"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-navigation-menu"
                className="size-11 text-white hover:bg-white/10"
              >
                <IconMenu2 className="size-5" />
              </Button>
            )}

            {/* Cart (Logged in only) */}
            {isUserLoggedIn && <CartBadgeButton />}

            {/* Divider (when cart is visible) */}
            {isUserLoggedIn && !compactNav && <div className="h-6 w-px bg-white/20" />}

            {/* Auth Actions */}
            {!compactNav && !isUserLoggedIn && (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={handleSignIn}
                  className="px-4 text-white/90 hover:text-white hover:bg-white/10"
                >
                  Sign in
                </Button>
                <Button onClick={handleSignup} className="px-5">
                  Create account
                </Button>
              </div>
            )}

            {isUserLoggedIn && !compactNav && (
              <Button onClick={() => openDashboard()} className="px-5">
                Dashboard
                <IconArrowRight className="size-4" />
              </Button>
            )}

            {/* Avatar - Visible on all screen sizes when logged in */}
            {isUserLoggedIn && <UserAvatarAndPopoverMenu onLogout={signOutTheUser} />}
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <MobileMenu
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        pages={navigationPages}
        goToPage={goToPage}
        isLoggedIn={isUserLoggedIn}
        onSignup={handleSignup}
        onSignIn={handleSignIn}
      />
    </>
  );
}

export default Header;
